import { supabase } from './SupabaseClient.js';
import { AuthClient } from './AuthClient.js';

// NOTE: adding a field here requires the matching column to exist on the
// Supabase `survivor_saves` table first, or every upsert (and the login
// select below) errors and silently drops ALL cloud sync — existing
// currencies included. Columns are defined (and idempotently migrated for
// existing deployments) in supabase/schema.sql — re-run that whole file to
// apply the cosmetic columns (glimmer / owned_cosmetics / equipped_cosmetics)
// before this ships. Local/guest play is unaffected (CloudSync only runs
// when signed in).
function toRow(userId, fields) {
  return {
    user_id: userId,
    echoes: fields.echoes,
    shards: fields.shards,
    meta_upgrades: fields.metaUpgrades,
    unlocked_characters: fields.unlockedCharacters,
    unlocked_perks: fields.unlockedPerks,
    owned_pets: fields.ownedPets,
    equipped_pets: fields.equippedPets,
    essence: fields.essence,
    glimmer: fields.glimmer,
    owned_cosmetics: fields.ownedCosmetics,
    equipped_cosmetics: fields.equippedCosmetics,
    redeemed_codes: fields.redeemedCodes,
    last_echo_snapshot: fields.lastEchoSnapshot,
    updated_at: new Date().toISOString(),
  };
}

function fromRow(row) {
  return {
    echoes: row.echoes,
    shards: row.shards,
    metaUpgrades: row.meta_upgrades,
    unlockedCharacters: row.unlocked_characters,
    unlockedPerks: row.unlocked_perks,
    // May be undefined on a row written before these columns existed —
    // SaveManager.replaceCloudFields keeps the local values in that case.
    ownedPets: row.owned_pets ?? undefined,
    equippedPets: row.equipped_pets ?? undefined,
    essence: row.essence ?? undefined,
    glimmer: row.glimmer ?? undefined,
    ownedCosmetics: row.owned_cosmetics ?? undefined,
    equippedCosmetics: row.equipped_cosmetics ?? undefined,
    redeemedCodes: row.redeemed_codes ?? undefined,
    lastEchoSnapshot: row.last_echo_snapshot ?? undefined,
  };
}

/**
 * Bridges SaveManager <-> the `survivor_saves` table. Cloud-relevant
 * SaveManager mutations (addEchoes, spendShards, setUpgradeLevel, ...) are
 * all infrequent, explicit player actions (once per run, once per shop
 * purchase) — not a per-frame hot path — so every push here is a plain
 * fire-and-forget upsert rather than something needing batching/debounce.
 * Every operation is best-effort and silent on failure: cloud sync is
 * additive on top of a fully playable local-guest game, same offline-
 * tolerant contract as LeaderboardClient.
 */
export class CloudSync {
  constructor(save) {
    this.save = save;
    this.userId = null;
    this._unsubscribeSave = null;
  }

  /**
   * Call once at boot. Restores an existing Supabase session if the
   * browser already had one and pulls its cloud save down. Returns true if
   * a session was restored, so the caller can re-render anything showing
   * echoes/shards/auth state.
   */
  async initSession() {
    if (!supabase) return false;
    const { data } = await supabase.auth.getSession();
    if (!data.session) return false;
    const username = await AuthClient.currentUsername();
    if (!username) return false; // session exists but its profile row is missing/broken — treat as signed out
    this.save.accountUsername = username;
    await this._attach(data.session.user.id);
    return true;
  }

  /** Call right after a successful sign-up/log-in. */
  async onSignedIn(userId, username, { seedFromLocal = false } = {}) {
    this.save.accountUsername = username;
    await this._attach(userId, { seedFromLocal });
  }

  /** Call on sign-out. Stops syncing and resets local progress to a fresh guest state. */
  onSignedOut() {
    this._unsubscribeSave?.();
    this._unsubscribeSave = null;
    this.userId = null;
    this.save.accountUsername = null;
    this.save.resetToGuest();
  }

  async _attach(userId, { seedFromLocal = false } = {}) {
    this.userId = userId;

    if (!seedFromLocal) {
      const { data } = await supabase
        .from('survivor_saves')
        .select('echoes, shards, meta_upgrades, unlocked_characters, unlocked_perks, owned_pets, equipped_pets, essence, glimmer, owned_cosmetics, equipped_cosmetics, redeemed_codes, last_echo_snapshot')
        .eq('user_id', userId)
        .maybeSingle();
      if (data) {
        this.save.replaceCloudFields(fromRow(data));
      } else {
        // Signed in but no row yet — either a brand-new account whose
        // signUp() couldn't seed one (email confirmation was pending), or
        // a first-ever login some other way. Either way, whatever's local
        // right now becomes the starting cloud save.
        await supabase.from('survivor_saves').upsert(toRow(userId, this.save.getCloudFields()));
      }
    } else {
      // Fresh signup with an immediate session: this device's current
      // guest progress becomes the starting cloud save.
      await supabase.from('survivor_saves').upsert(toRow(userId, this.save.getCloudFields()));
    }

    this._unsubscribeSave = this.save.onChange(() => this._push());
  }

  async _push() {
    if (!this.userId || !supabase) return;
    await supabase.from('survivor_saves').upsert(toRow(this.userId, this.save.getCloudFields()));
  }
}
